import { StyleSheet, Text, View } from 'react-native'
import { colors, fontFamily, fontSize, radius, space } from '../tokens'
import type { VerdictTier } from '../verdict-tier'

type CaseLabel = 'Good case' | 'Strong case' | 'Excellent case' | null | undefined

const verdictText = (tier: VerdictTier, caseLabel: CaseLabel) => {
  switch (tier) {
    case 'flagged':
      if (caseLabel === 'Excellent case')
        return 'Strong evidence of overbanding — clearly worth challenging'
      if (caseLabel === 'Strong case') return 'Likely overbanded — worth challenging'
      return 'Possibly overbanded — review the evidence below'
    case 'confident-ok':
      return 'Looks correctly banded'
    case 'thin':
      return 'Not enough data for a confident verdict'
  }
}

const verdictSubtext = (tier: VerdictTier, subjectSource: 'epc' | 'none' | undefined) => {
  if (tier !== 'thin') return null
  if (subjectSource === 'none') {
    return "We couldn't find an Energy Performance Certificate (EPC) for your specific property, so the verdict is based on postcode-level patterns rather than a strict like-for-like comparison."
  }
  return "We couldn't gather strong enough evidence on this property — see what we did and didn't find below. Worth a manual review of your bill against neighbours."
}

const SparkGlyph = () => (
  <Text style={styles.glyph} accessibilityElementsHidden>
    ✦
  </Text>
)
const ShieldGlyph = () => (
  <Text style={styles.glyph} accessibilityElementsHidden>
    ✓
  </Text>
)

type Props = {
  tier: VerdictTier
  caseLabel: CaseLabel
  flagged: boolean
  includedCount: number
  subjectSource: 'epc' | 'none' | undefined
}

export const BandCheckVerdict = ({
  tier,
  caseLabel,
  flagged,
  includedCount,
  subjectSource,
}: Props) => {
  const isPositive = tier === 'flagged'
  const containerStyle = isPositive ? styles.flag : styles.neutral
  const tileStyle = isPositive ? styles.tilePositive : styles.tileNeutral
  const titleStyle = isPositive ? styles.flagTitle : styles.neutralTitle
  const subtext = verdictSubtext(tier, subjectSource)
  return (
    <View style={[styles.verdict, containerStyle]}>
      <View style={[styles.tile, tileStyle]}>{isPositive ? <SparkGlyph /> : <ShieldGlyph />}</View>
      <View style={styles.body}>
        <Text style={[styles.title, titleStyle]}>{verdictText(tier, caseLabel)}</Text>
        {flagged && caseLabel ? (
          <Text style={styles.caseTag}>
            ✓ {caseLabel} · {includedCount} supporting strand{includedCount === 1 ? '' : 's'}
          </Text>
        ) : null}
        {subtext ? <Text style={styles.subtext}>{subtext}</Text> : null}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  verdict: {
    flexDirection: 'row',
    gap: space[16],
    padding: 20,
    borderRadius: radius.sm,
    marginVertical: space[16],
    alignItems: 'flex-start',
  },
  flag: {
    backgroundColor: colors.positiveSoft,
  },
  neutral: {
    backgroundColor: colors.surface2,
  },
  tile: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tilePositive: { backgroundColor: colors.positive },
  tileNeutral: { backgroundColor: colors.primary[700] },
  glyph: { color: '#FFFFFF', fontSize: 22, fontWeight: '800', lineHeight: 24 },
  body: { flex: 1, gap: space[4] },
  title: {
    fontSize: fontSize.h4,
    fontWeight: '700',
    letterSpacing: -0.2,
    fontFamily: fontFamily.body,
    lineHeight: 24,
  },
  flagTitle: { color: colors.positiveInk },
  neutralTitle: { color: colors.ink },
  caseTag: {
    alignSelf: 'flex-start',
    fontSize: 12,
    fontWeight: '600',
    color: colors.positiveInk,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: radius.pill,
    marginTop: 6,
    overflow: 'hidden',
    fontFamily: fontFamily.body,
  },
  subtext: {
    fontSize: fontSize.small,
    color: colors.muted,
    fontFamily: fontFamily.body,
    lineHeight: 20,
  },
})
